/** Formatage français. Une seule source de vérité pour les dates, heures et montants. */

export const TZ = "Europe/Paris";

const JOURS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const JOURS_COURTS = ["lun", "mar", "mer", "jeu", "ven", "sam", "dim"];
const MOIS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

export const joursSemaine = JOURS;
export const joursCourts = JOURS_COURTS;
export const moisNoms = MOIS;

/** Date locale (Europe/Paris) au format AAAA-MM-JJ. */
export function isoJour(d = new Date()) {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: TZ }).format(d);
}

export function deIso(iso) {
  const [a, m, j] = iso.split("-").map(Number);
  return new Date(Date.UTC(a, m - 1, j, 12));
}

export function ajouterJours(iso, n) {
  const d = deIso(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return isoJour(d);
}

export function lundiDe(iso) {
  const d = deIso(iso);
  const jour = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - jour);
  return isoJour(d);
}

export function jourSemaine(iso) {
  return (deIso(iso).getUTCDay() + 6) % 7;
}

export function jourLong(iso) {
  const d = deIso(iso);
  return `${JOURS[(d.getUTCDay() + 6) % 7]} ${d.getUTCDate()} ${MOIS[d.getUTCMonth()]}`;
}

export function jourCourt(iso) {
  const d = deIso(iso);
  return `${JOURS_COURTS[(d.getUTCDay() + 6) % 7]} ${d.getUTCDate()}`;
}

export function moisAnnee(iso) {
  const d = deIso(iso);
  return `${MOIS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export function heure(isoDatetime) {
  if (!isoDatetime) return "";
  const d = new Date(isoDatetime);
  const parts = new Intl.DateTimeFormat("fr-FR", {
    timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d);
  const h = Number(parts.find((p) => p.type === "hour").value);
  const m = parts.find((p) => p.type === "minute").value;
  return m === "00" ? `${h}h` : `${h}h${m}`;
}

export function minutesDepuisMinuit(isoDatetime) {
  const d = new Date(isoDatetime);
  const parts = new Intl.DateTimeFormat("fr-FR", {
    timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d);
  return (
    Number(parts.find((p) => p.type === "hour").value) * 60 +
    Number(parts.find((p) => p.type === "minute").value)
  );
}

export function euros(n, options = {}) {
  const v = Number(n || 0);
  const decimales = options.decimales ?? (Number.isInteger(v) ? 0 : 2);
  return `${v.toLocaleString("fr-FR", {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  })}\u00a0€`;
}

export function nb(v, dec = 1) {
  const n = Number(v || 0);
  return n.toLocaleString("fr-FR", {
    minimumFractionDigits: Number.isInteger(n) ? 0 : dec,
    maximumFractionDigits: dec,
  });
}

export function duree(minutes) {
  const m = Number(minutes || 0);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h${String(r).padStart(2, "0")}`;
  if (h) return `${h}h`;
  return `${r}\u00a0min`;
}

export function ilYA(jours) {
  if (jours === null || jours === undefined) return "jamais venue";
  if (jours === 0) return "aujourd'hui";
  if (jours === 1) return "hier";
  if (jours < 30) return `il y a ${jours} jours`;
  const mois = Math.round(jours / 30);
  if (mois < 12) return `il y a ${mois} mois`;
  const ans = Math.floor(mois / 12);
  return ans === 1 ? "il y a plus d'un an" : `il y a ${ans} ans`;
}

export function dansCombien(minutes) {
  if (minutes < 1) return "maintenant";
  if (minutes < 60) return `dans ${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) return m ? `dans ${h}h${String(m).padStart(2, "0")}` : `dans ${h}h`;
  return `dans ${Math.round(h / 24)} jours`;
}

export function telephoneLisible(v) {
  if (!v) return "";
  if (v.startsWith("+33") && v.length === 12) {
    const n = "0" + v.slice(3);
    return n.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
  }
  return v;
}

export function initiales(prenom, nom) {
  const a = (prenom || "").trim()[0] || "";
  const b = (nom || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}

export const STATUTS = {
  prevu: { label: "Prévu", ton: "neutre" },
  confirme: { label: "Confirmé", ton: "olive" },
  arrive: { label: "Arrivée", ton: "accent" },
  honore: { label: "Terminé", ton: "neutre" },
  absent: { label: "Absence", ton: "brique" },
  annule: { label: "Annulé", ton: "neutre" },
  annule_tardif: { label: "Annulé tard", ton: "brique" },
};

export const NIVEAUX_RISQUE = {
  faible: { label: "Risque faible", ton: "olive" },
  modere: { label: "Risque modéré", ton: "ambre" },
  eleve: { label: "Risque élevé", ton: "brique" },
};

export const METIERS = [
  { code: "coiffure", label: "Coiffure" },
  { code: "barbier", label: "Barbier" },
  { code: "ongles", label: "Ongles / nail art" },
  { code: "esthetique", label: "Esthétique" },
  { code: "massage", label: "Massage & bien-être" },
  { code: "cils", label: "Extensions de cils" },
  { code: "sourcils", label: "Sourcils" },
  { code: "maquillage", label: "Maquillage" },
  { code: "tatouage", label: "Tatouage" },
  { code: "autre", label: "Autre métier" },
];

/** Prestations proposées à l'onboarding : du concret, pas des exemples génériques. */
export const PRESTATIONS_TYPES = {
  coiffure: [
    { nom: "Coupe + brushing", duree_min: 45, buffer_min: 10, prix: 42 },
    { nom: "Couleur racines", duree_min: 90, buffer_min: 15, prix: 68 },
    { nom: "Balayage", duree_min: 150, buffer_min: 15, prix: 130 },
  ],
  barbier: [
    { nom: "Coupe homme", duree_min: 30, buffer_min: 5, prix: 25 },
    { nom: "Coupe + barbe", duree_min: 45, buffer_min: 5, prix: 38 },
    { nom: "Taille de barbe", duree_min: 20, buffer_min: 5, prix: 18 },
  ],
  ongles: [
    { nom: "Pose gel", duree_min: 90, buffer_min: 15, prix: 55 },
    { nom: "Remplissage", duree_min: 60, buffer_min: 10, prix: 40 },
    { nom: "Manucure russe", duree_min: 75, buffer_min: 10, prix: 48 },
  ],
  esthetique: [
    { nom: "Soin visage", duree_min: 60, buffer_min: 10, prix: 65 },
    { nom: "Épilation jambes", duree_min: 45, buffer_min: 10, prix: 35 },
    { nom: "Soin éclat express", duree_min: 30, buffer_min: 10, prix: 40 },
  ],
  massage: [
    { nom: "Massage relaxant 1h", duree_min: 60, buffer_min: 15, prix: 70 },
    { nom: "Massage dos & nuque", duree_min: 30, buffer_min: 10, prix: 40 },
    { nom: "Massage sportif 1h30", duree_min: 90, buffer_min: 15, prix: 95 },
  ],
  cils: [
    { nom: "Extensions cil à cil", duree_min: 120, buffer_min: 15, prix: 90 },
    { nom: "Remplissage cils", duree_min: 75, buffer_min: 10, prix: 55 },
    { nom: "Rehaussement de cils", duree_min: 60, buffer_min: 10, prix: 55 },
  ],
  sourcils: [
    { nom: "Restructuration sourcils", duree_min: 30, buffer_min: 5, prix: 25 },
    { nom: "Teinture sourcils", duree_min: 30, buffer_min: 5, prix: 25 },
    { nom: "Brow lift", duree_min: 60, buffer_min: 10, prix: 55 },
  ],
  maquillage: [
    { nom: "Maquillage jour", duree_min: 45, buffer_min: 10, prix: 55 },
    { nom: "Maquillage mariée", duree_min: 90, buffer_min: 20, prix: 140 },
    { nom: "Essai mariée", duree_min: 60, buffer_min: 15, prix: 70 },
  ],
  tatouage: [
    { nom: "Petit tatouage", duree_min: 60, buffer_min: 20, prix: 90 },
    { nom: "Session 3h", duree_min: 180, buffer_min: 30, prix: 300 },
    { nom: "Consultation projet", duree_min: 30, buffer_min: 10, prix: 0 },
  ],
  autre: [
    { nom: "Prestation courte", duree_min: 30, buffer_min: 10, prix: 35 },
    { nom: "Prestation complète", duree_min: 60, buffer_min: 15, prix: 60 },
  ],
};

export const HORAIRES_DEFAUT = {
  0: [{ start: "09:00", end: "18:00" }],
  1: [{ start: "09:00", end: "18:00" }],
  2: [{ start: "09:00", end: "18:00" }],
  3: [{ start: "09:00", end: "19:00" }],
  4: [{ start: "09:00", end: "19:00" }],
  5: [{ start: "10:00", end: "16:00" }],
};
